import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { CowreMark, IconAnimals, IconChart, IconCheckCircle, IconClose, IconDna } from '../../../common/icons';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { formatDateOnly } from '../../../common/utils/date';
import { stagesSum, embryosCreated, discardedCount, preservationDirective, STAGES } from '../../../core/constants/clivage';
import { useOpuSession } from '../../opu/contexts/opu';
import { useLabsList } from '../../labs/contexts/labs';
import SemenSearch from '../../semen/forms/search';
import { useCreateClivagesBulk } from '../contexts/clivages';
import { validateClivages } from '../utils/validate';
import ClivageRows from '../forms/clivage-rows';
import ReorganizeModal from '../forms/reorganize-modal';

import './styles/workspace.css';

const today = () => new Date().toLocaleDateString('en-CA');

function Kpi({ icon, label, value, accent }) {
    return (
        <div className={`cl-kpi ${accent ? 'cl-kpi--accent' : ''}`}>
            <span className="cl-kpi__icon">{icon}</span>
            <span className="cl-kpi__body">
                <span className="cl-kpi__value">{value}</span>
                <span className="cl-kpi__label">{label}</span>
            </span>
        </div>
    );
}

export default function ClivageWorkspace() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const sessionId = params.get('session');

    const { data: session, isLoading, error } = useOpuSession(sessionId, 'resolution');
    const { data: labsData } = useLabsList({ limit: 100 });
    const labs = labsData?.items ?? [];
    const { mutateAsync: createBulk, isPending } = useCreateClivagesBulk();
    const confirm = useConfirm();

    const [rows, setRows] = useState(null);
    const [errors, setErrors] = useState({});
    const [labId, setLabId] = useState('');
    const [date, setDate] = useState(today());
    const [sameSemen, setSameSemen] = useState(false); // off by default
    const [globalSemen, setGlobalSemen] = useState(null);
    const [allSexed, setAllSexedState] = useState(false);
    const [showReorg, setShowReorg] = useState(false);
    const [seeded, setSeeded] = useState(false);
    const [seq, setSeq] = useState(1); // claves únicas para clivages extra del mismo donante

    const aspirations = (session?.aspirations || []).filter(a => a.status !== 0);
    const donors = aspirations.map(a => ({
        aspiration_id: a.id,
        name: a.donor?.name || '—',
        number: a.donor?.number || '',
        viables: a.viables ?? 0,
        available: a.resolution?.oocytes_disponibles ?? a.viables ?? 0,
    }));
    const availableByAsp = Object.fromEntries(donors.map(d => [String(d.aspiration_id), d.available]));

    // Seed one row per donor with available oocytes, oocytes pre-filled to 100%
    // of the available (normally all viables are fertilized).
    if (session && !seeded) {
        setRows(donors.filter(d => d.available > 0).map(d => ({
            key: `d${d.aspiration_id}`,
            aspiration_id: d.aspiration_id,
            donorName: d.name,
            donorNumber: d.number,
            available: d.available,
            viables: d.viables,
            semen: null,
            oocytes: String(d.available),
            stages: {},
            sexed: false,
            preservation: 'fresh',
        })));
        setSeeded(true);
    }

    if (isLoading || (!session && !error)) return <div className="cl-ws__state">Cargando sesión OPU…</div>;
    if (error || !session) return <div className="cl-ws__state">Sesión OPU no encontrada.</div>;

    const clearErr = (key, field) =>
        setErrors(prev => (prev[key] ? { ...prev, [key]: { ...prev[key], [field]: undefined } } : prev));

    const setField = (key, field, value) => {
        // active guard: óvocitos de una fila no puede superar los DISPONIBLES de la donante.
        if (field === 'oocytes' && value !== '') {
            const row = (rows || []).find(r => r.key === key);
            const cap = row ? (row.available != null ? row.available : row.viables) : null;
            if (cap != null && Number.isFinite(Number(value)) && Number(value) > cap) {
                toast.error(`Los óvocitos no pueden superar los disponibles (${cap})`);
                return;
            }
        }
        setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
        clearErr(key, field);
    };
    const setStage = (key, stage, value) => {
        // active guard: Σ stages (embriones) no puede superar los óvocitos usados.
        if (value !== '') {
            const row = (rows || []).find(r => r.key === key);
            const nextEmb = stagesSum({ ...row?.stages, [stage]: value });
            if (row && row.oocytes !== '' && Number.isFinite(Number(row.oocytes)) && nextEmb > Number(row.oocytes)) {
                toast.error(`Los embriones no pueden superar los óvocitos (${row.oocytes})`);
                return;
            }
        }
        setRows(prev => prev.map(r => (r.key === key ? { ...r, stages: { ...r.stages, [stage]: value } } : r)));
        clearErr(key, 'embryos');
    };
    const setSexed = (key, value) => setRows(prev => prev.map(r => (r.key === key ? { ...r, sexed: value } : r)));
    const setSemen = (key, semen) => { setRows(prev => prev.map(r => (r.key === key ? { ...r, semen } : r))); clearErr(key, 'semen'); };
    const clearSemen = (key) => setRows(prev => prev.map(r => (r.key === key ? { ...r, semen: null } : r)));

    // Una fila se enviará (POST) si no está vacía Y tiene semen efectivo o embriones.
    // Mismo criterio que validateClivages.considered → KPIs y autollenado se alinean
    // con lo que realmente se guarda (no cuentan donadoras sembradas sin fertilizar).
    const effSemen = (r) => (sameSemen ? globalSemen : r.semen);
    const willPost = (r) => !(r.oocytes === '' && stagesSum(r.stages) === 0) && (!!effSemen(r) || stagesSum(r.stages) > 0);

    // Otro toro para la misma vaca: nuevo clivage (misma aspiración) insertado bajo
    // su grupo, autollenado con los óvocitos restantes (editable luego). Hereda "todo sexado".
    const addClivage = (aspirationId) => {
        const d = donors.find(x => x.aspiration_id === aspirationId);
        if (!d) return;
        setRows(prev => {
            // restantes = disponibles − Σ óvocitos de filas que SÍ se guardarán (ignora
            // primarias sembradas sin fertilizar, que no consumen óvocitos al guardar).
            const used = prev.filter(r => r.aspiration_id === aspirationId && willPost(r)).reduce((a, r) => a + (Number(r.oocytes) || 0), 0);
            const remaining = Math.max(0, (d.available ?? 0) - used);
            const newRow = { key: `x${aspirationId}-${seq}`, aspiration_id: aspirationId, donorName: d.name, donorNumber: d.number, available: d.available, viables: d.viables, semen: null, oocytes: remaining > 0 ? String(remaining) : '', stages: {}, sexed: allSexed, preservation: 'fresh' };
            let lastIdx = -1;
            prev.forEach((r, i) => { if (r.aspiration_id === aspirationId) lastIdx = i; });
            const next = [...prev];
            next.splice(lastIdx === -1 ? next.length : lastIdx + 1, 0, newRow); // sin grupo → al final, no al frente
            return next;
        });
        setSeq(s => s + 1);
    };
    const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key));

    // Recálculo de ÓV al enviar: si una fila con intención de fertilizar (semen o
    // embriones) quedó con la casilla de óvocitos VACÍA, se rellena con los disponibles
    // restantes de su donadora (repartidos si hay varias en blanco) para no perderla.
    const recalcOocytes = (current) => {
        const byAsp = new Map();
        current.forEach(r => { const k = String(r.aspiration_id); if (!byAsp.has(k)) byAsp.set(k, []); byAsp.get(k).push(r); });
        const fill = new Map();
        byAsp.forEach((group, k) => {
            const intent = (r) => (!!effSemen(r) || stagesSum(r.stages) > 0);
            const blanks = group.filter(r => intent(r) && r.oocytes === '');
            if (!blanks.length) return;
            const avail = availableByAsp[k] ?? group[0]?.viables ?? 0;
            const filled = group.filter(r => intent(r) && r.oocytes !== '').reduce((a, r) => a + (Number(r.oocytes) || 0), 0);
            const remaining = Math.max(0, avail - filled);
            const base = Math.floor(remaining / blanks.length);
            blanks.forEach((r, i) => {
                const val = (i === blanks.length - 1) ? remaining - base * (blanks.length - 1) : base;
                if (val > 0) fill.set(r.key, String(val)); // 0 → se deja en blanco (fila vacía, se omite)
            });
        });
        return fill.size ? current.map(r => (fill.has(r.key) ? { ...r, oocytes: fill.get(r.key) } : r)) : current;
    };

    const toggleAllSexed = (value) => {
        setAllSexedState(value);
        setRows(prev => prev.map(r => ({ ...r, sexed: value })));
    };

    // Reorganiza por DONANTE (grupo): mantiene juntos los clivages de cada vaca.
    const applyOrder = (ids) => {
        setRows(prev => {
            const byAsp = new Map();
            prev.forEach(r => { if (!byAsp.has(r.aspiration_id)) byAsp.set(r.aspiration_id, []); byAsp.get(r.aspiration_id).push(r); });
            return ids.flatMap(id => byAsp.get(id) || []);
        });
        setShowReorg(false);
    };

    const list = rows || [];
    // KPIs sobre lo que REALMENTE se guarda (posted = willPost), para que el tablero
    // refleje el lote enviado (no infla con donadoras sembradas sin fertilizar).
    // Tasa = embriones reales (sin DG) ÷ viables de donadoras fertilizadas.
    const posted = list.filter(willPost);
    // viables por DONANTE único (no por fila): varias clivages de una vaca comparten viables.
    const viablesByDonor = new Map();
    posted.forEach(r => { if (!viablesByDonor.has(r.aspiration_id)) viablesByDonor.set(r.aspiration_id, Number(r.viables) || 0); });
    const totalViables = [...viablesByDonor.values()].reduce((a, v) => a + v, 0);
    const totalEmb = posted.reduce((a, r) => a + embryosCreated(r.stages), 0);    // reales
    const totalDisc = posted.reduce((a, r) => a + discardedCount(r.stages), 0);   // DG descartados
    const totalStructures = list.reduce((a, r) => a + stagesSum(r.stages), 0);    // trabajo en curso (guard de cancelar)
    const combinaciones = posted.length;                                         // clivages que se guardarán (toro × donadora)
    // por DONANTE: fertilizadas (posted) sobre el total de donadoras sembradas.
    const donorCount = new Set(list.map(r => r.aspiration_id)).size;
    const donorsFert = new Set(posted.map(r => r.aspiration_id)).size;
    const tasa = totalViables ? Math.round((totalEmb / totalViables) * 100) : 0;

    const cancel = async () => {
        if (totalStructures > 0) {
            const ok = await confirm({
                title: 'Descartar fertilizaciones',
                message: '¿Descartar las fertilizaciones en progreso?',
                confirmLabel: 'Descartar',
                danger: true,
            });
            if (!ok) return;
        }
        navigate(`/admin/opu/${sessionId}`);
    };

    const handleSave = async () => {
        // recalcula los ÓV en blanco (de filas con semen/embriones) antes de validar.
        const rowsToSave = recalcOocytes(list);
        if (rowsToSave !== list) setRows(rowsToSave); // refleja el recálculo en la grilla
        const ctx = { sameSemen, globalSemen, lab: labId, availableByAsp };
        const { rowErrors, valid, considered, formError } = validateClivages(rowsToSave, ctx);
        if (!valid) {
            setErrors(rowErrors);
            toast.error(formError || (considered.length ? 'Revisá las filas marcadas' : 'Agregá al menos una fertilización'));
            return;
        }
        setErrors({});

        const items = considered.map(r => {
            const embryos = stagesSum(r.stages);          // embryos_count (incluye DG)
            const created = embryosCreated(r.stages);     // reales (sin DG)
            const semen = sameSemen ? globalSemen : r.semen;
            const stagesObj = {};
            STAGES.forEach(s => { const n = Number(r.stages?.[s.value]) || 0; if (n > 0) stagesObj[s.value] = n; });
            return {
                aspiration_id: Number(r.aspiration_id),
                semen_id: semen.id,
                lab_id: Number(labId),
                date: date || today(),
                oocytes: Number(r.oocytes),
                embryos_count: embryos,
                ...(embryos > 0 ? { stages: stagesObj } : {}),
                ...(created > 0 ? { preservation: preservationDirective(r.preservation) } : {}),
                ...(r.sexed ? { type: 'sexado' } : {}),
            };
        });

        try {
            await createBulk(items);
            toast.success(`${items.length} fertilización(es) registradas`);
            navigate(`/admin/opu/${sessionId}`);
        } catch (err) {
            if (err?.status === 422) toast.error(err?.message || 'Conservación: óvocitos exceden lo disponible');
            else if (err?.status === 409) toast.error(err?.message || 'Conflicto de referencia');
            else toast.error(err?.message || 'No se pudieron registrar las fertilizaciones');
        }
    };

    return (
        <div className="cl-ws">
            <header className="cl-ws__topbar">
                <div className="cl-ws__brand">
                    <span className="cl-ws__mark"><CowreMark size={30} id="cl-mark" /></span>
                    <span className="cl-ws__brand-text">COWRE</span>
                    <span className="cl-ws__divider" />
                    <span className="cl-ws__title">Clivage · {session.code}</span>
                </div>
                <button type="button" className="cl-ws__close" onClick={cancel} aria-label="Cerrar"><IconClose /></button>
            </header>

            <div className="cl-ws__content">
                <section className="cl-ws__controls">
                    <div className="cl-ws__control">
                        <span className="cl-ws__control-label">Laboratorio</span>
                        <select value={labId} onChange={(e) => setLabId(e.target.value)} disabled={isPending}>
                            <option value="">Seleccionar…</option>
                            {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                    </div>
                    <div className="cl-ws__control">
                        <span className="cl-ws__control-label">Fecha</span>
                        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={isPending} />
                    </div>

                    <label className="cl-ws__toggle">
                        <input type="checkbox" checked={sameSemen} onChange={(e) => setSameSemen(e.target.checked)} disabled={isPending} />
                        <span>Mismo semen</span>
                    </label>
                    <label className="cl-ws__toggle">
                        <input type="checkbox" checked={allSexed} onChange={(e) => toggleAllSexed(e.target.checked)} disabled={isPending} />
                        <span>Todo sexado</span>
                    </label>

                    {sameSemen && (
                        <div className="cl-ws__control cl-ws__control--semen">
                            <span className="cl-ws__control-label">Semen común</span>
                            <SemenSearch value={globalSemen} onSelect={setGlobalSemen} onClear={() => setGlobalSemen(null)} />
                        </div>
                    )}

                    <button type="button" className="cl-ws__reorg" onClick={() => setShowReorg(true)} disabled={isPending || !list.length}>
                        Reorganizar
                    </button>
                </section>

                <section className="cl-ws__kpis">
                    <Kpi icon={<IconDna />} label="Combinaciones" value={combinaciones} />
                    <Kpi icon={<IconCheckCircle />} label="Embriones" value={totalEmb} />
                    <Kpi icon={<IconChart />} label="Tasa emb./viables" value={`${tasa}%`} accent />
                    <Kpi icon={<IconAnimals />} label="Donadoras fert." value={`${donorsFert}/${donorCount}`} />
                </section>

                <section className="cl-ws__meta">
                    <span><strong>{session.farm?.name || '—'}</strong></span>
                    <span>· {formatDateOnly(session.date)}</span>
                </section>

                {!list.length ? (
                    <div className="cl-ws__state">No hay donantes con óvocitos disponibles para clivar.</div>
                ) : (
                    <ClivageRows
                        rows={list}
                        errors={errors}
                        sameSemen={sameSemen}
                        globalSemen={globalSemen}
                        onField={setField}
                        onStage={setStage}
                        onSemen={setSemen}
                        onClearSemen={clearSemen}
                        onSexed={setSexed}
                        onAddClivage={addClivage}
                        onRemove={removeRow}
                    />
                )}
            </div>

            <footer className="cl-ws__footer">
                <span className="cl-ws__footer-hint">{combinaciones} combinación(es) · {totalEmb} embrión(es){totalDisc > 0 ? ` · ${totalDisc} DG descartado(s)` : ''}</span>
                <div className="cl-ws__actions">
                    <button type="button" className="cl-ws__cancel" onClick={cancel} disabled={isPending}>Cancelar</button>
                    <button type="button" className="cl-ws__primary" onClick={handleSave} disabled={isPending}>
                        {isPending ? 'Guardando…' : 'Guardar clivages'}
                    </button>
                </div>
            </footer>

            {showReorg && (
                <ReorganizeModal
                    items={[...new Map(list.map(r => [r.aspiration_id, { id: r.aspiration_id, label: `${r.donorName}${r.donorNumber ? ` · ${r.donorNumber}` : ''}` }])).values()]}
                    onApply={applyOrder}
                    onClose={() => setShowReorg(false)}
                />
            )}
        </div>
    );
}
